import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
} from "recharts";
import { MapContainer, TileLayer, CircleMarker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import "./DashboardHome.css";
import { getAuthToken } from "../../utils/authStorage";

/* ── palette matching the defect categories ─────────────────────── */
const CLASS_COLORS = {
  AisladorRoto:        "#ef4444",
  DescargaParcial:     "#ef4444",
  FijacionDefectuosa:  "#ef4444",
  AisladorCorrosion:   "#f59e0b",
  TorreFisurada:       "#f59e0b",
  HerrajeCorrosion:    "#f59e0b",
  ObjetoAjeno:         "#f59e0b",
  TorreCorrosion:      "#f59e0b",
  AisladorContaminado: "#facc15",
};
const fallbackColors = ["#2563eb","#16a34a","#f59e0b","#ef4444","#8b5cf6","#06b6d4","#f97316","#64748b"];
const getColor = (cls, i) => CLASS_COLORS[cls] ?? fallbackColors[i % fallbackColors.length];

function DefectTooltip({ active, payload, total }) {
  if (!active || !payload?.length) return null;

  const item = payload[0];
  const value = Number(item.value ?? 0);
  const percent = total > 0 ? Math.round((value / total) * 100) : 0;

  return (
    <div className="dh-chart-tooltip">
      <div className="dh-chart-tooltip-title">
        <span className="dh-chart-tooltip-dot" style={{ background: item.payload.fill }} />
        {item.name}
      </div>
      <div className="dh-chart-tooltip-value">
        {value} detections · {percent}%
      </div>
    </div>
  );
}

/* ── metric card ─────────────────────────────────────────────────── */
function MetricCard({ label, value, sub, accent }) {
  return (
    <div className="dh-metric-card" style={{ borderTop: `4px solid ${accent}` }}>
      <div className="dh-metric-label">{label}</div>
      <div className="dh-metric-value">{value ?? "—"}</div>
      {sub && <div className="dh-metric-sub">{sub}</div>}
    </div>
  );
}

/* ── main component ─────────────────────────────────────────────── */
export default function DashboardHome() {
  const navigate = useNavigate();
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);

  useEffect(() => {
    const token = getAuthToken();
    axios
      .get("/api/dashboard/summary", { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setData(res.data))
      .catch(() => setError("Could not load dashboard data."))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="dh-page dh-center">Loading dashboard…</div>;
  if (error)   return <div className="dh-page dh-center dh-error">{error}</div>;

  const pins  = data.inspectionPins    ?? [];
  const recent = data.recentInspections ?? [];
  const byClass = Object.entries(data.detectionsByClass ?? {}).map(([name, value]) => ({ name, value }));

  // Map center: average of all pins, fallback to Argentina center
  const mapCenter = pins.length
    ? [ pins.reduce((s, p) => s + p.lat, 0) / pins.length,
        pins.reduce((s, p) => s + p.lon, 0) / pins.length ]
    : [-33.4, -66.0];

  return (
    <div className="dh-page">

      {/* ── Metric cards ── */}
      <div className="dh-metrics">
        <MetricCard label="Projects"        value={data.totalProjects}        accent="var(--app-brand)" />
        <MetricCard label="Inspections"     value={data.totalInspections}     accent="var(--app-info-text)" />
        <MetricCard label="Images Analyzed" value={data.totalImagesAnalyzed}  accent="var(--app-success)" />
        <MetricCard label="Defects Found"   value={data.totalDetections}      accent="var(--app-danger)"
          sub={data.totalDetections > 0 ? `${Object.keys(data.detectionsByClass ?? {}).length} defect types` : null} />
      </div>

      {/* ── Main row: map + recent inspections ── */}
      <div className="dh-main-row">

        {/* Map */}
        <div className="dh-card dh-map-card">
          <div className="dh-card-title">Inspection Locations</div>
          <div className="dh-map-wrap">
            <MapContainer
              center={mapCenter}
              zoom={pins.length ? 7 : 5}
              style={{ width: "100%", height: "100%" }}
              scrollWheelZoom
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {pins.map((pin) => (
                <CircleMarker
                  key={pin.inspectionId}
                  center={[pin.lat, pin.lon]}
                  radius={pin.detectionCount > 0 ? 10 : 7}
                  pathOptions={{
                    color: pin.detectionCount > 0 ? "var(--app-danger)" : "var(--app-brand)",
                    fillColor: pin.detectionCount > 0 ? "var(--app-danger)" : "var(--app-brand)",
                    fillOpacity: 0.8,
                    weight: 2,
                  }}
                >
                  <Popup>
                    <div className="dh-popup">
                      <div className="dh-popup-name">{pin.inspectionName}</div>
                      <div className="dh-popup-proj">{pin.projectName}</div>
                      <div className="dh-popup-stats">
                        <span>🖼 {pin.imageCount} images</span>
                        <span>⚠ {pin.detectionCount} defects</span>
                      </div>
                      <button
                        className="dh-popup-btn"
                        onClick={() => navigate(`/inspections/${pin.inspectionId}/results`)}
                      >
                        View results →
                      </button>
                    </div>
                  </Popup>
                </CircleMarker>
              ))}
            </MapContainer>
          </div>
          <div className="dh-map-legend">
            <span className="dh-legend-dot" style={{ background: "var(--app-danger)" }} /> With defects
            <span className="dh-legend-dot" style={{ background: "var(--app-brand)", marginLeft: 14 }} /> No defects
          </div>
        </div>

        {/* Recent inspections */}
        <div className="dh-card dh-recent-card">
          <div className="dh-card-title">Recent Inspections</div>
          <div className="dh-recent-list">
            {recent.length === 0 && (
              <div className="dh-empty">No inspections yet.</div>
            )}
            {recent.map((insp) => (
              <div
                key={insp.inspectionId}
                className="dh-recent-row"
                onClick={() => navigate(`/projects/${insp.projectId}`)}
              >
                <div className="dh-recent-left">
                  <div className="dh-recent-name">{insp.inspectionName}</div>
                  <div className="dh-recent-proj">{insp.projectName}</div>
                </div>
                <div className="dh-recent-right">
                  {insp.detectionCount > 0 ? (
                    <span className="dh-badge dh-badge--alert">⚠ {insp.detectionCount}</span>
                  ) : insp.imageCount > 0 ? (
                    <span className="dh-badge dh-badge--ok">✓ Clean</span>
                  ) : (
                    <span className="dh-badge dh-badge--pending">Pending</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Bottom row: defects chart ── */}
      {byClass.length > 0 && (
        <div className="dh-bottom-row">
          <div className="dh-card dh-chart-card">
            <div className="dh-card-title">Defects by Type</div>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={byClass}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={58}
                  outerRadius={90}
                  paddingAngle={byClass.length > 1 ? 1 : 0}
                  isAnimationActive={false}
                >
                  {byClass.map((entry, i) => (
                    <Cell key={entry.name} fill={getColor(entry.name, i)} />
                  ))}
                </Pie>
                <Tooltip content={<DefectTooltip total={data.totalDetections} />} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="dh-card dh-breakdown-card">
            <div className="dh-card-title">Defect Breakdown</div>
            <div className="dh-breakdown-list">
              {byClass.map((entry, i) => {
                const pct = data.totalDetections > 0
                  ? Math.round((entry.value / data.totalDetections) * 100)
                  : 0;
                return (
                  <div key={entry.name} className="dh-breakdown-row">
                    <span className="dh-breakdown-dot" style={{ background: getColor(entry.name, i) }} />
                    <span className="dh-breakdown-name">{entry.name}</span>
                    <div className="dh-breakdown-bar-wrap">
                      <div className="dh-breakdown-bar"
                        style={{ width: `${pct}%`, background: getColor(entry.name, i) }} />
                    </div>
                    <span className="dh-breakdown-count">{entry.value}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
